import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { AppBar, Toolbar, Typography, IconButton, Box, Avatar, Backdrop, CircularProgress } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { logout } from "modules/login/redux/session.action";
import logoSmartKood from "assets/img/logo-smartkood.png";

const useStyles = makeStyles((theme) => ({
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor: "#FFFFFF",
    color: "#3A4D54",
    boxShadow: "0px 2px 4px -2px rgba(16,24,40,0.06), 0px 4px 8px -2px rgba(16,24,40,0.1)",
  },
  toolbar: {
    minHeight: 68,
    padding: "0 20px",
    display: "flex",
    justifyContent: "space-between",
  },
  leftSection: {
    display: "flex",
    alignItems: "center",
    gap: 20,
  },
  logo: {
    height: 30,
    objectFit: "contain",
  },
  badge: {
    border: "2px solid #3A4D54",
    borderRadius: 42,
    padding: "4px 12px",
    fontWeight: 700,
    fontSize: 14,
    lineHeight: "20px",
    color: "#3A4D54",
  },
  rightSection: {
    display: "flex",
    alignItems: "center",
    gap: 17,
  },
  divider: {
    width: 1,
    height: 30,
    backgroundColor: "rgba(0,0,0,0.15)",
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  userTextBlock: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
  },
  userRole: {
    fontSize: 10,
    fontWeight: 700,
    color: "#3A4D54",
    lineHeight: 1.4,
  },
  userName: {
    fontSize: 12,
    fontWeight: 400,
    color: "#3A4D54",
    lineHeight: 1.4,
  },
  avatar: {
    width: 36,
    height: 36,
    backgroundColor: "#3A4D54",
    fontSize: 14,
    fontWeight: 700,
  },
  logoutBtn: {
    color: "#3A4D54",
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 100,
    color: "#FFFFFF",
    flexDirection: "column",
    gap: 16,
  },
  backdropText: {
    fontSize: 16,
    fontWeight: 500,
  },
}));

export default function Navbar({ drawerWidth }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const user = useSelector((state) => state.session.user);
  const userName = useSelector((state) => state.session.name);
  const roleName = useSelector((state) => state.session.roleName);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await dispatch(logout());
    } finally {
      const stillLoggedIn = !!localStorage.getItem("admin_access_token");
      if (stillLoggedIn) {
        setIsLoggingOut(false);
        return;
      }
      history.push("/login");
    }
  };

  const getInitials = (name) => {
    if (!name) return "A";
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  return (
    <AppBar position="fixed" className={classes.appBar}>
      <Toolbar className={classes.toolbar}>
        <Box className={classes.leftSection}>
          <img src={logoSmartKood} alt="SmartKood" className={classes.logo} />
          <Box className={classes.badge}>Admin Portal</Box>
        </Box>
        <Box className={classes.rightSection}>
          <Box className={classes.divider} />
          <Box className={classes.userInfo}>
            <Box className={classes.userTextBlock}>
              <Typography className={classes.userRole}>{roleName || "Admin"}</Typography>
              <Typography className={classes.userName}>
                {userName || user?.name || user?.email || "Admin"}
              </Typography>
            </Box>
            <Avatar className={classes.avatar}>
              {getInitials(userName || user?.name || user?.email)}
            </Avatar>
          </Box>
          <IconButton
            className={classes.logoutBtn}
            onClick={handleLogout}
            size="small"
            disabled={isLoggingOut}
          >
            <ExitToAppIcon />
          </IconButton>
        </Box>
      </Toolbar>
      <Backdrop className={classes.backdrop} open={isLoggingOut}>
        <CircularProgress color="inherit" />
        <Typography className={classes.backdropText}>Logging out...</Typography>
      </Backdrop>
    </AppBar>
  );
}
